import type { CSSProperties } from "react";

/**
 * Pixel gutter for callers that need a number (e.g. legacy math). `%` gaps
 * return 0 — use layout measurement or `parseColumnGapTokens` instead.
 */
export function parseBuilderGapPx(gap: string | undefined): number {
	if (gap == null || typeof gap !== "string") {
		return 24;
	}
	const trimmed = gap.trim();
	const t = parseColumnGapTokens(gap);
	if (t.gapPercentEach > 0) {
		return 0;
	}
	if (/^(\d+(?:\.\d+)?)px$/i.test(trimmed)) {
		return t.gapPx;
	}
	return 24;
}

/** Gutter between columns: px and/or % (only one should be non-zero for a single token). */
export function parseColumnGapTokens(gap: string | undefined): {
	gapPx: number;
	gapPercentEach: number;
} {
	if (gap == null || typeof gap !== "string") {
		return { gapPx: 24, gapPercentEach: 0 };
	}
	const t = gap.trim();
	const px = /^(\d+(?:\.\d+)?)px$/i.exec(t);
	if (px) {
		return { gapPx: Number(px[1]), gapPercentEach: 0 };
	}
	const pc = /^(\d+(?:\.\d+)?)%$/i.exec(t);
	if (pc) {
		return { gapPx: 0, gapPercentEach: Number(pc[1]) };
	}
	return { gapPx: 24, gapPercentEach: 0 };
}

export type WrappedColumnsRowChildArgs = {
	widthFraction: number;
	weightSum: number;
	columnCount: number;
	gap: string | undefined;
	/** When column uses percent width (`widthType === "percent"`). */
	widthPercent?: number | null;
	/**
	 * `0` when the row uses `flex-wrap: wrap` (keep basis, let extra width wrap).
	 * `1` when `nowrap` so items can shrink and stay on one row with `gap`.
	 */
	flexItemShrink?: 0 | 1;
};

/**
 * Flex item style for a column in `display:flex; flex-direction:row; flex-wrap:wrap`.
 * Plain `flex: n` uses basis 0 + shrink, so siblings stay on one row; this sets a
 * positive basis and `flex-shrink: 0` so wrapping can occur when the row is too narrow.
 */
export function wrappedColumnsRowChildOuterStyle(
	args: WrappedColumnsRowChildArgs
): CSSProperties {
	const wf = args.widthFraction > 0 ? args.widthFraction : 1;
	const n = Math.max(1, args.columnCount);
	const sum = args.weightSum > 0 ? args.weightSum : 1;
	const fraction = wf / sum;
	const { gapPx, gapPercentEach } = parseColumnGapTokens(args.gap);
	const gapPxTotal = (n - 1) * gapPx;
	const gapPctTotal = (n - 1) * gapPercentEach;
	const flexShrink: 0 | 1 = args.flexItemShrink ?? 0;

	if (
		args.widthPercent != null &&
		typeof args.widthPercent === "number" &&
		Number.isFinite(args.widthPercent)
	) {
		const p = args.widthPercent / 100;
		if (n <= 1) {
			return {
				boxSizing: "border-box",
				flexBasis: `${args.widthPercent}%`,
				flexGrow: 0,
				flexShrink,
				maxWidth: "100%",
				minWidth: 0,
			};
		}
		/** Share of the row *after* total gutter is reserved, so % + gap stays within 100%. */
		const flexBasis =
			gapPctTotal > 0
				? `calc((100% - ${gapPctTotal}%) * ${p})`
				: `calc((100% - ${gapPxTotal}px) * ${p})`;
		return {
			boxSizing: "border-box",
			flexBasis,
			flexGrow: 0,
			flexShrink,
			maxWidth: "100%",
			minWidth: 0,
		};
	}
	if (n <= 1) {
		return { flex: wf };
	}
	const flexBasisAutoSplit =
		gapPctTotal > 0
			? `calc((100% - ${gapPctTotal}%) * ${fraction})`
			: `calc((100% - ${gapPxTotal}px) * ${fraction})`;
	return {
		boxSizing: "border-box",
		flexBasis: flexBasisAutoSplit,
		flexGrow: 0,
		flexShrink,
		maxWidth: "100%",
		minWidth: 0,
	};
}

/** Clamp wrap-row column count used by columns auto-grid (`wrapColumns`). */
export function clampColumnsWrapColumns(value: unknown): number {
	if (typeof value !== "number" || !Number.isFinite(value)) {
		return 1;
	}
	return Math.min(12, Math.max(1, Math.round(value)));
}

/**
 * Equal-width flex item style when a columns row uses wrap auto-grid
 * (`wrap` + `wrapColumns` ≥ 2).
 *
 * Sets `width` / `maxWidth` to the same calc as `flexBasis` so columns stay
 * equal even when percentage `flex-basis` resolves as `auto` (indefinite flex
 * container main size) — a common builder-canvas vs public-layout mismatch.
 */
export function columnsAutoGridChildOuterStyle(args: {
	wrapColumns: number;
	gap: string | undefined;
}): CSSProperties {
	const n = clampColumnsWrapColumns(args.wrapColumns);
	const base = wrappedColumnsRowChildOuterStyle({
		widthFraction: 1,
		weightSum: n,
		columnCount: n,
		gap: args.gap,
		flexItemShrink: 0,
	});
	const basis = base.flexBasis;
	if (typeof basis !== "string") {
		return base;
	}
	return {
		...base,
		width: basis,
		maxWidth: basis,
	};
}

/**
 * CSS length for `--pb-col-gap`. Preserves explicit `0` / `0%` (do not coerce
 * to the 24px default — that only applies when gap is missing/invalid).
 */
export function columnsGapCssVar(gap: string | undefined): string {
	const { gapPx, gapPercentEach } = parseColumnGapTokens(gap);
	if (gapPercentEach > 0) {
		return `${gapPercentEach}%`;
	}
	return `${gapPx}px`;
}

/**
 * Injected once with {@link BuilderPreview}: rows with `data-pb-columns-stack-mobile="1"`
 * stack their direct children at ≤767px (matches builder mobile + wrap).
 * Rows with `data-pb-columns-wrap-cols` use equal-width auto-grid wrap instead.
 */
/** Shared fallback when `--pb-col-basis` is missing (legacy markup). */
const PB_COL_BASIS_FALLBACK =
	"calc((100% - (var(--pb-wrap-cols, 2) - 1) * var(--pb-col-gap, 24px)) / var(--pb-wrap-cols, 2))";

export const BUILDER_COLUMNS_MOBILE_STACK_CSS = `@media (max-width: 767px) {
  [data-pb-columns-stack-mobile="1"] {
    flex-direction: column !important;
    flex-wrap: nowrap !important;
  }
  [data-pb-columns-stack-mobile="1"] > * {
    align-self: stretch !important;
    box-sizing: border-box !important;
    flex: 0 0 auto !important;
    max-width: 100% !important;
    min-width: 0 !important;
    width: 100% !important;
  }
  [data-pb-columns-wrap-cols] {
    flex-direction: row !important;
    flex-wrap: wrap !important;
    box-sizing: border-box !important;
    width: 100% !important;
    max-width: 100% !important;
  }
  [data-pb-columns-wrap-cols] > * {
    align-self: stretch !important;
    box-sizing: border-box !important;
    flex-grow: 0 !important;
    flex-shrink: 0 !important;
    flex-basis: var(--pb-col-basis, ${PB_COL_BASIS_FALLBACK}) !important;
    max-width: var(--pb-col-basis, ${PB_COL_BASIS_FALLBACK}) !important;
    min-width: 0 !important;
    width: var(--pb-col-basis, ${PB_COL_BASIS_FALLBACK}) !important;
  }
}`;

/** Same stack / auto-grid rules keyed by builder device toolbar (`data-builder-viewport`). */
export const BUILDER_COLUMNS_MOBILE_STACK_VIEWPORT_ATTR_CSS = `[data-builder-viewport="mobile"] [data-pb-columns-stack-mobile="1"] {
  flex-direction: column !important;
  flex-wrap: nowrap !important;
}
[data-builder-viewport="mobile"] [data-pb-columns-stack-mobile="1"] > * {
  align-self: stretch !important;
  box-sizing: border-box !important;
  flex: 0 0 auto !important;
  max-width: 100% !important;
  min-width: 0 !important;
  width: 100% !important;
}
[data-builder-viewport="mobile"] [data-pb-columns-wrap-cols] {
  flex-direction: row !important;
  flex-wrap: wrap !important;
  box-sizing: border-box !important;
  width: 100% !important;
  max-width: 100% !important;
}
[data-builder-viewport="mobile"] [data-pb-columns-wrap-cols] > * {
  align-self: stretch !important;
  box-sizing: border-box !important;
  flex-grow: 0 !important;
  flex-shrink: 0 !important;
  flex-basis: var(--pb-col-basis, ${PB_COL_BASIS_FALLBACK}) !important;
  max-width: var(--pb-col-basis, ${PB_COL_BASIS_FALLBACK}) !important;
  min-width: 0 !important;
  width: var(--pb-col-basis, ${PB_COL_BASIS_FALLBACK}) !important;
}`;
